/** @format */

import React from 'react';
import {SHEET_LEADING_EDGE_WIDTH, SHEET_NON_LEADING_EDGE_WIDTH} from 'frontier-constants';
import ShapeSvg from '@/components/die_template/shape_svg';

const svgLayers = [
    {id: 'dieline', label: 'Die Line', description: 'Final cut line'},
    {id: 'bleedline', label: 'Bleed Line', description: 'Extend artwork to this line'},
    {id: 'kisscutline', label: 'Kiss Cut Line', description: 'Cut through top layer only'},
    {id: 'creaseline', label: 'Crease Line', description: 'Fold line'},
    {id: 'knockout', label: 'Knockout', description: 'Ink and Primer removed'}
];

export {svgLayers};

export default function Layout({layoutMatrix, sizeSvg}) {
    // Overall dimensions of the printable area from the layout matrix
    const printableWidth = layoutMatrix.printableLeadingEdgeWidth;
    const printableHeight = layoutMatrix.printableNonLeadingEdgeWidth;

    var cartonLeadingEdgeOffset = 15 / 25.4; // 15mm in inches

    // Calculate margins to center the printable area on the sheet
    const marginX = (SHEET_LEADING_EDGE_WIDTH - printableWidth) / 2;
    const marginY = cartonLeadingEdgeOffset;

    // Constants for the corner border marks
    const borderMarkLength = 0.5; // length of the corner lines (adjust as needed)
    const borderStroke = 'black';
    const borderStrokeWidth = 0.014; // stroke width for both lines and rectangle
    // SVG strokes are centered on the path but we expect the stroke edge to be outside the path
    const strokeAlignmentOffset = borderStrokeWidth / 2; // half of the stroke width for alignment

    const cornerSquareLength = 0.25 - borderStrokeWidth;
    // Define a y-offset for the text so that it sits above the crop marks.
    const textYOffset = -0.3; // Adjust this value as needed

    return (
        <svg
            viewBox={`0 0 ${SHEET_LEADING_EDGE_WIDTH} ${SHEET_NON_LEADING_EDGE_WIDTH}`}
            width={`${SHEET_LEADING_EDGE_WIDTH}in`}
            height={`${SHEET_NON_LEADING_EDGE_WIDTH}in`}
            xmlns="http://www.w3.org/2000/svg"
            data-filename="components/die_template_layout/layout.jsx">
            {/* Translate group to center the printable area on the full sheet */}
            <g transform={`translate(${marginX}, ${marginY})`}>
                {layoutMatrix.layout.map((columns, rowIndex) =>
                    columns.map((layout, columnIndex) => {
                        return (
                            <g
                                key={`${rowIndex}-${columnIndex}`}
                                transform={`translate(${layout.left}, ${layout.top}) rotate(${
                                    layout.rotation
                                }, ${layoutMatrix.flatWidth / 2}, ${layoutMatrix.flatHeight / 2})`}
                                data-filename="components/die_template_layout/layout.jsx">
                                {sizeSvg && sizeSvg.length > 0 ? (
                                    <image
                                        href={`data:image/svg+xml;utf8,${encodeURIComponent(
                                            sizeSvg
                                        )}`}
                                        width={`${layoutMatrix.flatWidth}`}
                                        height={`${layoutMatrix.flatHeight}`}
                                    />
                                ) : (
                                    <ShapeSvg
                                        useViewBox={false}
                                        visibleLayers={svgLayers.map(l => l.id)}
                                        width={layoutMatrix.width}
                                        length={layoutMatrix.length}
                                        depth={layoutMatrix.depth}
                                        shapeId={layoutMatrix.shapeId}
                                        flatWidth={layoutMatrix.flatWidth}
                                        flatHeight={layoutMatrix.flatHeight}
                                        isShowingBleedlines={true}
                                        isShowingCrease={true}
                                        isShowingDielines={true}
                                        isShowingGlueLines={true}
                                        isShowingSafeZone={true}
                                        isLayoutOnly
                                    />
                                )}
                            </g>
                        );
                    })
                )}

                {/* Add LEADING EDGE text above the crop marks with a smaller font size */}
                <text
                    x={printableWidth / 2}
                    y={textYOffset}
                    textAnchor="middle"
                    style={{fontFamily: 'sans-serif', fontSize: '0.002in'}}>
                    LEADING EDGE
                </text>

                {/* Corner border marks for the printable area */}
                <g className="corner-marks">
                    {/* Top Left Corner */}
                    <line
                        x1="0"
                        y1={strokeAlignmentOffset}
                        x2={borderMarkLength}
                        y2={strokeAlignmentOffset}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />
                    <line
                        x1={strokeAlignmentOffset}
                        y1="0"
                        x2={strokeAlignmentOffset}
                        y2={borderMarkLength}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />

                    <rect
                        x={strokeAlignmentOffset}
                        y={strokeAlignmentOffset} // adjust for the stroke alignment
                        width={cornerSquareLength}
                        height={cornerSquareLength}
                        fill="#000000"
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />

                    {/* Top Right Corner */}
                    <line
                        x1={printableWidth - borderMarkLength}
                        y1={strokeAlignmentOffset}
                        x2={printableWidth}
                        y2={strokeAlignmentOffset}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />
                    <line
                        x1={printableWidth - strokeAlignmentOffset}
                        y1="0"
                        x2={printableWidth - strokeAlignmentOffset}
                        y2={borderMarkLength}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />

                    <rect
                        x={printableWidth - cornerSquareLength - strokeAlignmentOffset}
                        y={strokeAlignmentOffset} // adjust for the stroke alignment
                        width={cornerSquareLength}
                        height={cornerSquareLength}
                        fill="#000000"
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />

                    {/* Bottom Left Corner */}
                    <line
                        x1={strokeAlignmentOffset}
                        y1={printableHeight - borderMarkLength}
                        x2={strokeAlignmentOffset}
                        y2={printableHeight}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />
                    <line
                        x1="0"
                        y1={printableHeight - strokeAlignmentOffset}
                        x2={borderMarkLength}
                        y2={printableHeight - strokeAlignmentOffset}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />

                    {/* Bottom Right Corner */}
                    <line
                        x1={printableWidth - strokeAlignmentOffset}
                        y1={printableHeight - borderMarkLength}
                        x2={printableWidth - strokeAlignmentOffset}
                        y2={printableHeight}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />
                    <line
                        x1={printableWidth - borderMarkLength}
                        y1={printableHeight - strokeAlignmentOffset}
                        x2={printableWidth}
                        y2={printableHeight - strokeAlignmentOffset}
                        stroke={borderStroke}
                        strokeWidth={borderStrokeWidth}
                    />
                </g>
            </g>
        </svg>
    );
}
